import { useMutation, useQuery } from "@tanstack/react-query";
import { Alert, App, Modal, Progress, Space, Table, Tag, Typography } from "antd";
import { useState } from "react";
import { api, unwrap, type Schemas } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useI18n } from "@/i18n";
import { useRefreshMapping } from "./mapping";

type Group = Schemas["CreateSKUPlanGroup"];
type Filters = { connectionId?: string; platform?: Schemas["ChannelPlatformName"]; q?: string };

/** Why a group is left out, or null when it is created or mapped. */
function skipped(g: Group): "invalid" | "archived" | null {
  if (g.invalid) return "invalid";
  return g.existing === "archived" ? "archived" : null;
}

/**
 * "Buat SKU untuk semua": every unmapped platform SKU the filters show gets
 * an internal SKU coded by its seller SKU, after a look at what that does.
 */
export function CreateAllSkus({ open, filters, onClose }: { open: boolean; filters: Filters; onClose: () => void }) {
  const { t, formatNumber } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const refresh = useRefreshMapping();
  const [done, setDone] = useState(0);
  const plan = useQuery({
    queryKey: ["platform-skus", "create-plan", filters],
    queryFn: () => unwrap(api.GET("/v1/catalog/platform-skus/create-plan", { params: { query: filters } })),
    enabled: open,
  });
  const groups = plan.data?.groups ?? [];
  const todo = groups.filter((g) => !skipped(g));
  const ids = todo.flatMap((g) => g.ids);
  const fresh = todo.filter((g) => g.existing === "").length;
  const left = groups.length - todo.length + (plan.data?.noSellerSku ?? 0);

  const create = useMutation({
    mutationFn: async () => {
      setDone(0);
      let failed = 0;
      // create-skus takes 200 at a time; each platform SKU stands on its own.
      for (let i = 0; i < ids.length; i += 200) {
        const { results } = await unwrap(api.POST("/v1/catalog/platform-skus/create-skus", { body: { ids: ids.slice(i, i + 200) } }));
        failed += results.filter((r) => !r.ok).length;
        setDone(Math.min(i + 200, ids.length));
      }
      return failed;
    },
    onSuccess: (failed) => {
      void message.success(t("listings.createAll.done", { n: formatNumber(ids.length - failed) }));
      if (failed > 0) void message.warning(t("listings.createAll.failed", { n: formatNumber(failed) }));
      refresh();
      onClose();
    },
    onError: (err) => {
      refresh();
      void message.error(errorText(err));
    },
  });

  return (
    <Modal
      open={open}
      width={820}
      title={t("listings.createAll.title")}
      onCancel={() => !create.isPending && onClose()}
      okText={t("listings.createAll.ok", { n: formatNumber(ids.length) })}
      okButtonProps={{ disabled: ids.length === 0 }}
      onOk={() => create.mutate()}
      confirmLoading={create.isPending}
      cancelButtonProps={{ disabled: create.isPending }}
      destroyOnHidden
    >
      <Space orientation="vertical" size={12} style={{ width: "100%" }}>
        <Typography.Text type="secondary">{t("listings.createAll.help")}</Typography.Text>
        {plan.data && (
          <Alert
            type="info"
            showIcon
            title={t("listings.createAll.summary", {
              fresh: formatNumber(fresh),
              reused: formatNumber(todo.length - fresh),
              skipped: formatNumber(left),
            })}
          />
        )}
        {plan.data?.truncated && <Alert type="warning" showIcon title={t("listings.createAll.truncated")} />}
        {create.isPending && <Progress percent={Math.round((done / Math.max(ids.length, 1)) * 100)} />}
        <Table<Group>
          size="small"
          rowKey="code"
          loading={plan.isFetching}
          dataSource={groups}
          pagination={{ pageSize: 50, hideOnSinglePage: true, showSizeChanger: false }}
          scroll={{ y: 360 }}
          locale={{ emptyText: t("listings.createAll.empty") }}
          columns={[
            { title: t("catalog.code"), dataIndex: "code", width: 200, render: (v: string) => <span className="z-num">{v}</span> },
            { title: t("common.name"), dataIndex: "name" },
            { title: t("listings.createAll.listings"), width: 90, align: "right", render: (_, g) => <span className="z-num">{g.ids.length}</span> },
            {
              title: t("listings.createAll.action"),
              width: 190,
              render: (_, g) => {
                const why = skipped(g);
                if (why) return <Tag>{t(`listings.createAll.skip.${why}`)}</Tag>;
                return g.existing === "active" ? <Tag color="processing">{t("listings.createAll.reuse")}</Tag> : <Tag color="success">{t("listings.createAll.new")}</Tag>;
              },
            },
          ]}
        />
      </Space>
    </Modal>
  );
}
